import {
	useEffect,
	useMemo,
	useState,
	forwardRef,
	useImperativeHandle,
	useCallback
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ColDef } from 'ag-grid-community';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import { toast } from 'sonner';
import { Megaphone } from 'lucide-react';

import { ListPageLayout } from '@/components/layout/list-page-layout';
import { openEntityInNewWindow } from '@/lib/open-in-new-window';
import { useResourceNavigation, useGo } from '@/hooks/useResourceNavigation';
import {
	ActiveInactiveCellRenderer,
	DeleteCellRenderer
} from '@/components/grid';
import { useConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { showMutationSuccessToast } from '@/lib/mutationRefetch';
import { extractCheckResultError } from '@/lib/checkResult';
import { DEFAULT_PAGE_SIZE } from '@/lib/constants';

import {
	CAMPAIGNS_LIST_QUERY,
	CAMPAIGN_DELETE_MUTATION
} from './campaigns.gql.tsx';
import { CampaignDetails } from './campaign-details';
import { StatusBadge } from '@/components/ui/status-badge';
import {
	selectCampaignAfterSave,
	type CampaignListRecord
} from './campaign-list-selection';
import { cloneCampaign } from './cloneCampaign';

interface CampaignListItem {
	campaignId: string;
	name: string;
	description?: string | null;
	startDate: string;
	endDate: string;
	isActive: boolean;
}

interface CampaignListQueryResponse {
	campaigns?: {
		nodes?: Array<{
			campaignId: string;
			name: string;
			description?: string | null;
			startDate: string;
			endDate: string;
			isActive: boolean;
			facilityId: string;
			facility?: {
				facilityId: string;
				name?: string | null;
			} | null;
		}>;
		pageInfo?: {
			hasNextPage: boolean;
			endCursor?: string | null;
		};
	};
}

const formatDate = (value: string | null | undefined) => {
	if (!value) {
		return '—';
	}

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return value;
	}

	return date.toLocaleDateString();
};

const CampaignEntityIcon = () => (
	<div className="flex items-center justify-center w-14 h-14 rounded-full bg-transparent border-2 border-gray-400 dark:border-gray-500 flex-shrink-0">
		<Megaphone className="w-7 h-7 text-gray-600 dark:text-gray-300" />
	</div>
);

export interface CampaignsListRef {
	selectCampaignAfterSave: (campaignId: string) => Promise<void>;
}

export const CampaignsList = forwardRef<CampaignsListRef>((_, ref) => {
	const { t } = useTranslation('campaigns');
	const [itemsToFetch, setItemsToFetch] = useState(DEFAULT_PAGE_SIZE);
	const { data, loading, refetch, fetchMore } =
		useQuery<CampaignListQueryResponse>(CAMPAIGNS_LIST_QUERY, {
			variables: { first: itemsToFetch },
			fetchPolicy: 'cache-and-network'
		});
	const { goToCreate, goToEdit } = useResourceNavigation('campaigns');
	const go = useGo();
	const { showDialog } = useConfirmationDialog();
	const [localCampaigns, setLocalCampaigns] = useState<CampaignListRecord[]>(
		[]
	);
	const apolloClient = useApolloClient();
	const location = useLocation();
	const navigate = useNavigate();

	const [deleteCampaign, { loading: deleteLoading }] = useMutation(
		CAMPAIGN_DELETE_MUTATION
	);

	// Pagination state
	const pageInfo = data?.campaigns?.pageInfo;
	const hasNextPage = pageInfo?.hasNextPage ?? false;
	const endCursor = pageInfo?.endCursor;
	const [isLoadingMore, setIsLoadingMore] = useState(false);

	const handleLoadMore = useCallback(async () => {
		if (!hasNextPage || isLoadingMore || !endCursor) return;

		setIsLoadingMore(true);
		try {
			await fetchMore({
				variables: {
					first: DEFAULT_PAGE_SIZE,
					after: endCursor
				},
				updateQuery: (prev, { fetchMoreResult }) => {
					if (!fetchMoreResult) return prev;
					const newNodes = fetchMoreResult.campaigns?.nodes || [];
					const prevNodes = prev.campaigns?.nodes || [];
					setItemsToFetch(prevNodes.length + newNodes.length);
					setLocalCampaigns([]);
					return {
						...prev,
						campaigns: {
							...fetchMoreResult.campaigns,
							nodes: [...prevNodes, ...newNodes]
						}
					};
				}
			});
		} finally {
			setIsLoadingMore(false);
		}
	}, [hasNextPage, isLoadingMore, endCursor, fetchMore]);

	const handleRefresh = useCallback(() => {
		setItemsToFetch(DEFAULT_PAGE_SIZE);
		setLocalCampaigns([]);
		refetch({ first: DEFAULT_PAGE_SIZE });
	}, [refetch]);

	const serverCampaigns = useMemo<CampaignListItem[]>(() => {
		const nodes = data?.campaigns?.nodes ?? [];

		return nodes
			.filter((node): node is NonNullable<typeof node> => Boolean(node))
			.map(node => ({
				campaignId: node.campaignId,
				name: node.name,
				description: node.description,
				startDate: node.startDate,
				endDate: node.endDate,
				isActive: node.isActive
			}))
			.sort((a, b) => a.name.localeCompare(b.name));
	}, [data?.campaigns?.nodes]);

	// Use local campaigns data if available, otherwise fall back to server data
	const campaigns = useMemo(() => {
		// Always prefer server data - local state is only for optimistic updates
		if (localCampaigns.length === 0 || serverCampaigns.length === 0) {
			return serverCampaigns;
		}
		return localCampaigns;
	}, [serverCampaigns, localCampaigns]);

	// Expose method to select campaign after save via ref
	useImperativeHandle(
		ref,
		() => ({
			selectCampaignAfterSave: async (campaignId: string) => {
				await selectCampaignAfterSave({
					campaignId,
					client: apolloClient,
					currentCampaigns: campaigns,
					onDataUpdate: setLocalCampaigns,
					onCampaignSelect: setSelectedCampaign
				});
			}
		}),
		[apolloClient, campaigns]
	);

	// Handle campaign selection from location state (after create/edit)
	useEffect(() => {
		const state = location.state as { selectCampaignId?: string } | null;
		if (state?.selectCampaignId && campaigns.length > 0 && !loading) {
			const campaignIdToSelect = state.selectCampaignId;
			// Trigger the selection after a short delay to ensure grid is ready
			const timeoutId = setTimeout(async () => {
				try {
					await selectCampaignAfterSave({
						campaignId: campaignIdToSelect,
						client: apolloClient,
						currentCampaigns: campaigns,
						onDataUpdate: setLocalCampaigns,
						onCampaignSelect: setSelectedCampaign
					});
					// Clear the location state after processing to prevent re-triggering
					navigate(location.pathname, { replace: true, state: null });
				} catch (error) {
					console.error(
						'Failed to select campaign after navigation:',
						error
					);
				}
			}, 100);

			return () => clearTimeout(timeoutId);
		}
	}, [
		location.state,
		campaigns.length,
		loading,
		apolloClient,
		campaigns,
		navigate,
		location.pathname
	]);

	const [selectedCampaign, setSelectedCampaign] =
		useState<CampaignListItem | null>(null);

	useEffect(() => {
		if (!selectedCampaign && campaigns.length > 0) {
			setSelectedCampaign(campaigns[0]);
			return;
		}

		if (selectedCampaign) {
			const updated = campaigns.find(
				item => item.campaignId === selectedCampaign.campaignId
			);

			if (!updated) {
				setSelectedCampaign(null);
				return;
			}

			if (updated !== selectedCampaign) {
				setSelectedCampaign(updated);
			}
		}
	}, [campaigns, selectedCampaign]);

	// Helper function to find the next campaign to select after deletion
	const findNextCampaignToSelect = (
		deletedCampaign: CampaignListItem,
		campaignList: CampaignListItem[]
	) => {
		const currentIndex = campaignList.findIndex(
			c => c.campaignId === deletedCampaign.campaignId
		);

		if (campaignList.length <= 1) {
			return null;
		}

		// Try to select the next campaign (same index after deletion)
		if (currentIndex < campaignList.length - 1) {
			return campaignList[currentIndex + 1];
		}

		// If deleting the last campaign, select the previous one
		if (currentIndex > 0) {
			return campaignList[currentIndex - 1];
		}

		return null;
	};

	// Helper function to handle post-deletion selection
	const handlePostDeletionSelection = (
		nextCampaignToSelect: CampaignListItem | null,
		updatedCampaigns: CampaignListItem[]
	) => {
		setLocalCampaigns([]);
		// First, clear the current selection completely
		setSelectedCampaign(null);

		// Then after a brief delay, set the next campaign
		setTimeout(() => {
			if (nextCampaignToSelect && updatedCampaigns.length > 0) {
				// Find the campaign in the updated list by ID
				const updatedCampaign = updatedCampaigns.find(
					c => c.campaignId === nextCampaignToSelect?.campaignId
				);

				if (updatedCampaign) {
					setSelectedCampaign(updatedCampaign);
				}
			}
		}, 100);
	};

	const handleDeleteCampaign = async (campaign: CampaignListItem) => {
		const confirmed = await showDialog(
			'Delete Campaign',
			`Are you sure you want to delete campaign "${campaign.name}"? This action cannot be undone.`
		);

		if (confirmed) {
			// Determine if we need to find the next campaign to select
			const wasSelectedCampaign =
				selectedCampaign?.campaignId === campaign.campaignId;

			// Clear selection immediately if deleting the selected campaign
			if (wasSelectedCampaign) {
				setSelectedCampaign(null);
			}

			try {
				const result = await deleteCampaign({
					variables: {
						input: {
							campaignId: campaign.campaignId
						}
					},
					refetchQueries: ['Campaigns']
				});

				const checkResult = result.data?.deleteCampaign;

				if (checkResult?.success) {
					showMutationSuccessToast('campaigns', 'delete');

					// Find the next campaign to select after deletion (before refetch)
					let nextCampaignToSelect = null;
					if (wasSelectedCampaign) {
						nextCampaignToSelect = findNextCampaignToSelect(
							campaign,
							campaigns
						);
					}

					// Refetch data
					refetch().then(result => {
						const updatedCampaigns =
							result.data?.campaigns?.nodes ?? [];
						const mappedCampaigns = updatedCampaigns
							.filter(Boolean)
							.map(node => ({
								campaignId: node.campaignId,
								name: node.name,
								description: node.description,
								startDate: node.startDate,
								endDate: node.endDate,
								isActive: node.isActive
							}))
							.sort((a, b) => a.name.localeCompare(b.name));
						handlePostDeletionSelection(
							nextCampaignToSelect,
							mappedCampaigns
						);
					});
				} else {
					const errorMessage = extractCheckResultError(checkResult);
					toast.error(`Failed to delete campaign: ${errorMessage}`);
				}
			} catch (error) {
				console.error('Delete campaign error:', error);
				toast.error('Failed to delete campaign. Please try again.');
			}
		}
	};

	const handleDeleteCampaignById = async (campaignId: string) => {
		const campaign = campaigns.find(c => c.campaignId === campaignId);
		if (campaign) {
			await handleDeleteCampaign(campaign);
		}
	};

	const handleCloneCampaign = (campaignId: string) => {
		const campaign = campaigns.find(c => c.campaignId === campaignId);
		if (campaign) {
			const cloneData = cloneCampaign({
				name: campaign.name ?? '',
				description: campaign.description ?? '',
				startDate: campaign.startDate,
				endDate: campaign.endDate,
				isActive: campaign.isActive
			});
			go({
				to: '/campaigns/create',
				state: { cloneData }
			});
		}
	};

	const columns: ColDef<CampaignListItem>[] = [
		{
			headerName: t('list.columns.campaign'),
			field: 'name',
			flex: 1
		},
		{
			headerName: t('list.columns.starts'),
			field: 'startDate',
			width: 120,
			valueGetter: params => formatDate(params.data?.startDate)
		},
		{
			headerName: t('list.columns.ends'),
			field: 'endDate',
			width: 120,
			valueGetter: params => formatDate(params.data?.endDate)
		},
		{
			headerName: t('list.columns.status'),
			field: 'isActive',
			width: 120,
			minWidth: 120,
			cellRenderer: ActiveInactiveCellRenderer
		},
		{
			headerName: '',
			field: undefined,
			width: 60,
			minWidth: 60,
			maxWidth: 60,
			cellRenderer: DeleteCellRenderer,
			cellRendererParams: {
				onDelete: handleDeleteCampaign
			},
			sortable: false,
			filter: false,
			resizable: false
		}
	];

	return (
		<ListPageLayout
			columns={columns}
			data={campaigns}
			title={t('list.title')}
			description={t('list.description')}
			searchPlaceholder={t('list.searchPlaceholder')}
			isLoading={loading || deleteLoading}
			onRowClick={item => setSelectedCampaign(item)}
			onCreate={goToCreate}
			onRefresh={handleRefresh}
			selectedItem={selectedCampaign}
			detailsTitle="Campaign details"
			onEdit={id => goToEdit(id)}
			onDelete={handleDeleteCampaignById}
			onClone={handleCloneCampaign}
			onOpenInNewWindow={id => openEntityInNewWindow('campaigns', id)}
			renderDetailsContent={item => (
				<CampaignDetails campaignId={item.campaignId} />
			)}
			getRecordId={item => item.campaignId}
			getDetailsDescription={item => item.name}
			getStatusBadge={item => <StatusBadge isActive={item.isActive} />}
			renderEntityIcon={() => <CampaignEntityIcon />}
			getEntityTypeName={item =>
				item.isActive
					? t('details.activeCampaign')
					: t('details.inactiveCampaign')
			}
			hasNextPage={hasNextPage}
			isLoadingMore={isLoadingMore}
			onLoadMore={handleLoadMore}
			entityName={t('entityName')}
		/>
	);
});

CampaignsList.displayName = 'CampaignsList';
